import React from "react";
import type { WeeklyMetric, WeeklyPageChange, WeeklySearch } from "../../../services/visibilityService";

// Weekly Search Console comparison (SGL-470): this week against the week before,
// computed exactly by the backend. Every number and date is shown as sent.
// Shown only when there are numbers: "not connected", "reconnect" and failed
// reads are already said by the Search performance section below.

const fmtNum = (x: number) => x.toLocaleString();
const fmtPct = (x: number) => `${(x * 100).toFixed(1)}%`;
const day = (d: string) => new Date(`${d}T00:00:00`);
const MONTH: Intl.DateTimeFormatOptions = { month: "short" };

/** "Oct 16–22", or "Sep 28 – Oct 4" across months. */
function fmtRange([start, end]: [string, string]): string {
  const a = day(start), b = day(end);
  const am = a.toLocaleDateString(undefined, MONTH), bm = b.toLocaleDateString(undefined, MONTH);
  return am === bm ? `${am} ${a.getDate()}–${b.getDate()}` : `${am} ${a.getDate()} – ${bm} ${b.getDate()}`;
}

/** Just the path for a page on the site; the full URL stays in the tooltip. */
function pagePath(url: string): string {
  try {
    return new URL(url).pathname || url;
  } catch {
    return url;
  }
}

const tone = (better: boolean | null) =>
  better === null ? undefined : better ? "var(--avm-good)" : "var(--avm-bad)";

/** Second label line: the change, then last week's value. */
const Change: React.FC<{ m: WeeklyMetric; fmt: (x: number) => string; pct?: boolean; lowerIsBetter?: boolean }> = ({
  m, fmt, pct, lowerIsBetter,
}) => {
  const diff = m.this - m.last;
  const better = diff === 0 ? null : lowerIsBetter ? diff < 0 : diff > 0;
  if (pct && (m.delta_pct === null || m.delta_pct === undefined)) {
    return <div className="avm-lbl">none last week</div>;
  }
  return (
    <div className="avm-lbl">
      {pct && (
        <span style={{ color: tone(better) }}>{m.delta_pct! > 0 ? "+" : ""}{m.delta_pct}% </span>
      )}
      {!pct && better !== null && <span style={{ color: tone(better) }}>{better ? "▲" : "▼"} </span>}
      vs {fmt(m.last)}
    </div>
  );
};

/** The metric that moved past the threshold: clicks first, impressions otherwise. */
function moved(r: WeeklyPageChange): { pct: number; what: string } | null {
  const c = r.clicks.delta_pct, i = r.impressions.delta_pct;
  if (c !== null && c !== undefined && Math.abs(c) > 20) return { pct: c, what: "clicks" };
  if (i !== null && i !== undefined) return { pct: i, what: "impressions" };
  return null;
}

const PageList: React.FC<{ title: string; rows: WeeklyPageChange[]; empty: string; down: boolean }> = ({
  title, rows, empty, down,
}) => (
  <>
    <p className="avm-eyebrow" style={{ margin: "22px 0 4px" }}>{title}</p>
    {rows.length === 0 ? (
      <p className="avm-empty-sub" style={{ margin: 0 }}>{empty}</p>
    ) : (
      <div className="avm-src">
        {rows.map((r) => {
          const m = moved(r);
          return (
            <div className="avm-src-row" key={r.page}>
              <span className="avm-dot" style={{ background: down ? "var(--avm-bad)" : "var(--avm-good)" }} />
              <span className="avm-name" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.page}>
                {pagePath(r.page)}
              </span>
              <span className="avm-num" style={{ fontSize: 12, color: "var(--avm-muted)", marginRight: 8 }}>
                {fmtNum(r.clicks.last)} → {fmtNum(r.clicks.this)} clicks
              </span>
              {m && (
                <span className="avm-num" style={{ fontSize: 12, fontWeight: 700, color: tone(!down) }}>
                  {m.pct > 0 ? "+" : ""}{Math.round(m.pct)}% {m.what}
                </span>
              )}
            </div>
          );
        })}
      </div>
    )}
  </>
);

const WeeklySearchCard: React.FC<{ value: WeeklySearch | null; loading: boolean }> = ({ value, loading }) => {
  if (!value) {
    if (!loading) return null;
    return (
      <section className="avm-card" style={{ marginTop: 16 }} aria-busy="true">
        <header><h2>This week vs last week</h2></header>
        <p className="avm-empty-sub" style={{ margin: 0 }}>Loading…</p>
      </section>
    );
  }
  if (value.status !== "ok" && value.status !== "insufficient_data") return null;
  const t = value.totals;
  return (
    <section className="avm-card" style={{ marginTop: 16 }} aria-labelledby="avm-weekly-title">
      <header>
        <h2 id="avm-weekly-title">This week vs last week</h2>
        <span className="avm-meta">{fmtRange(value.windows.this)} vs {fmtRange(value.windows.last)}</span>
      </header>

      {t && (
        <div className="avm-kpis">
          <div className="avm-kpi">
            <b className="avm-num">{fmtNum(t.clicks.this)}</b>
            <div className="avm-lbl">Clicks from Google</div>
            <Change m={t.clicks} fmt={fmtNum} pct />
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{fmtNum(t.impressions.this)}</b>
            <div className="avm-lbl">Search impressions</div>
            <Change m={t.impressions} fmt={fmtNum} pct />
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{fmtPct(t.ctr.this)}</b>
            <div className="avm-lbl">Click-through rate</div>
            <Change m={t.ctr} fmt={fmtPct} />
          </div>
          <div className="avm-kpi">
            <b className="avm-num">{t.position.this > 0 ? t.position.this.toFixed(1) : "—"}</b>
            <div className="avm-lbl">Avg position</div>
            <Change m={t.position} fmt={(x) => x.toFixed(1)} lowerIsBetter />
          </div>
        </div>
      )}

      {value.status === "insufficient_data" && (
        <p className="avm-empty-sub" style={{ margin: "16px 0 0" }}>
          Not enough search data yet to spot opportunities ({value.reason}).
        </p>
      )}

      {value.status === "ok" && (
        <>
          <p className="avm-eyebrow" style={{ margin: "22px 0 4px" }}>On page 2 · cheapest wins</p>
          {(value.striking ?? []).length === 0 ? (
            <p className="avm-empty-sub" style={{ margin: 0 }}>No searches on page 2 with at least 20 impressions this week.</p>
          ) : (
            <div className="avm-src">
              {(value.striking ?? []).map((s) => (
                <div className="avm-src-row" key={s.query}>
                  <span className="avm-dot" style={{ background: "var(--avm-accent)" }} />
                  <span className="avm-name" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.page ?? undefined}>{s.query}</span>
                  <span className="avm-num" style={{ fontSize: 12, color: "var(--avm-muted)", marginRight: 8 }}>{fmtNum(s.impressions)} impr.</span>
                  <span className="avm-cell mid" style={{ width: "auto", padding: "0 8px" }}>{s.position.toFixed(1)}</span>
                </div>
              ))}
            </div>
          )}
          <PageList title="Pages dropping" rows={value.drops ?? []} down empty="No page dropped more than 20% this week." />
          <PageList title="Pages gaining" rows={value.winners ?? []} down={false} empty="No page gained more than 20% this week." />
        </>
      )}
    </section>
  );
};

export default WeeklySearchCard;
